import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AdminBadgeRole } from '@ui-components/admin/badge';
import { AdminConfirmDialogComponent, IAdminConfirmDialogData } from '@ui-components/admin/confirm-dialog';
import { OrgPickerComponent } from '@ui-components/admin/org-picker';
import {
  AdminPeopleService,
  IAdminMovePersonResult,
} from '../../services/admin-people.service';
import { IAdminOrgOption } from '../../services/admin-organisations.service';
import { AdminMoveSelectionService, ISelectedPerson } from '../../services/admin-move-selection.service';

interface DestinationForm {
  destination: FormControl<string>;
}

const OUTCOME_ROLE: Record<IAdminMovePersonResult['outcome'], AdminBadgeRole> = {
  moved: 'success',
  skipped: 'warning',
  failed: 'danger',
};

const OUTCOME_LABEL: Record<IAdminMovePersonResult['outcome'], string> = {
  moved: 'Moved',
  skipped: 'Skipped',
  failed: 'Failed',
};

const MOVE_PEOPLE_TRIGGER_SELECTOR = '[data-testid="move-people"]';
const DESTINATION_INPUT_SELECTOR = '[data-testid="move-panel-destination-input"]';

// A view over AdminMoveSelectionService (TK-077): the panel holds no move
// state of its own, so a dismiss and a reopen show exactly what the service
// holds. A shell companion region, not a dialog — no scrim, no focus trap.
@Component({
  selector: 'app-move-panel',
  standalone: false,
  templateUrl: './move-panel.component.html',
  styleUrl: './move-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MovePanelComponent implements AfterViewInit, OnInit {
  @ViewChild(OrgPickerComponent) private _picker?: OrgPickerComponent;

  destinationForm: FormGroup<DestinationForm>;
  destinationError: string | null = null;

  constructor(
    protected _selection: AdminMoveSelectionService,
    protected _people_service: AdminPeopleService,
    protected _dialog: MatDialog,
    private _fb: NonNullableFormBuilder,
    private _destroy_ref: DestroyRef,
    private _cdr: ChangeDetectorRef,
    private _element_ref: ElementRef<HTMLElement>
  ) {
    this.destinationForm = this._fb.group<DestinationForm>({
      destination: this._fb.control(this._selection.destinationOrgName),
    });
  }

  ngOnInit(): void {
    this._subscribeToGatheringChanges();
  }

  get selectedPeople(): ISelectedPerson[] {
    return this._selection.selectedPeople;
  }

  get selectionCount(): number {
    return this._selection.selectionCount;
  }

  get results(): IAdminMovePersonResult[] | null {
    return this._selection.results;
  }

  get destinationOrgName(): string {
    return this._selection.destinationOrgName;
  }

  ngAfterViewInit(): void {
    this._element_ref.nativeElement
      .querySelector<HTMLInputElement>(DESTINATION_INPUT_SELECTOR)
      ?.focus();
  }

  has(orgPersonId: number): boolean {
    return this._selection.has(orgPersonId);
  }

  remove(orgPersonId: number): void {
    this._selection.remove(orgPersonId);
    this._cdr.markForCheck();
  }

  trackBySelectedPersonId(_index: number, person: ISelectedPerson): number {
    return person.org_person_id;
  }

  trackByResultId(_index: number, result: IAdminMovePersonResult): number {
    return result.org_person_id;
  }

  fullName(person: ISelectedPerson): string {
    return `${person.first_name} ${person.last_name}`;
  }

  badgeRole(outcome: IAdminMovePersonResult['outcome']): AdminBadgeRole {
    return OUTCOME_ROLE[outcome];
  }

  outcomeLabel(outcome: IAdminMovePersonResult['outcome']): string {
    return OUTCOME_LABEL[outcome];
  }

  onDestinationSelected(option: IAdminOrgOption): void {
    this._selection.setDestination(option.id, option.name ?? '');
    this.destinationError = null;
    this._cdr.markForCheck();
  }

  onDestinationInvalidated(): void {
    this._selection.clearDestination();
    this._cdr.markForCheck();
  }

  onMoveClick(): void {
    if (!this._selection.destinationOrgId) {
      this.destinationError = 'Choose a destination organization before moving.';
      this._cdr.markForCheck();
      return;
    }
    this._openMoveConfirm();
  }

  // Header close (TK-077): dismisses without clearing the gathering.
  onDismissClick(): void {
    this._selection.dismiss();
    this._cdr.markForCheck();
    this._returnFocus();
  }

  // Footer "Cancel move": the discard path, confirmation-gated when there is
  // something to discard.
  onCancelClick(): void {
    if (this._selection.selectionCount === 0) {
      this._selection.dismiss();
      this._cdr.markForCheck();
      this._returnFocus();
      return;
    }
    this._openCancelConfirm();
  }

  private _returnFocus(): void {
    document.querySelector<HTMLElement>(MOVE_PEOPLE_TRIGGER_SELECTOR)?.focus();
  }

  private _openMoveConfirm(): void {
    const count = this._selection.selectionCount;
    const noun = count === 1 ? 'person' : 'people';
    const data: IAdminConfirmDialogData = {
      title: `Move ${count} ${noun} to ${this.destinationOrgName}?`,
      body: `Each person's assessment history moves with them to ${this.destinationOrgName}.`,
      consequence: `This affects ${count} ${noun}.`,
      confirmLabel: `Move ${count} ${noun}`,
      destructive: false,
    };

    const dialog_ref = this._dialog.open(AdminConfirmDialogComponent, {
      width: '480px',
      maxHeight: '90vh',
      data,
    });

    dialog_ref.componentInstance.confirm.pipe(
      takeUntilDestroyed(this._destroy_ref)
    ).subscribe(() => {
      this._move(dialog_ref);
    });
  }

  private _move(dialog_ref: MatDialogRef<AdminConfirmDialogComponent>): void {
    const instance = dialog_ref.componentInstance;
    instance.setSubmitting(true);
    instance.setError(null);

    const org_person_ids = Array.from(this._selection.gathered.keys());
    const destination_org_id = this._selection.destinationOrgId as number;

    this._people_service.movePeople({ org_person_ids, destination_org_id }).pipe(
      takeUntilDestroyed(this._destroy_ref)
    ).subscribe({
      next: (result) => {
        dialog_ref.close(true);
        this._selection.completeMove(result.results);
        this._resetDestination();
      },
      error: (err: { error?: { message?: string } }) => {
        const message = err?.error?.message ?? 'Could not move people — try again.';
        instance.setSubmitting(false);
        instance.setError(message);
      },
    });
  }

  // The list ticks people on a separate OnPush branch of the tree — this
  // view must be explicitly marked dirty when the gathering changes there.
  private _subscribeToGatheringChanges(): void {
    this._selection.gatheringChanged$.pipe(
      takeUntilDestroyed(this._destroy_ref)
    ).subscribe(() => {
      this._cdr.markForCheck();
    });
  }

  // View half of the post-move reset (the service clears id and name). The
  // silent control reset plus the picker reset is deliberate: an emitting
  // setValue('') would fire the picker's lookup and invalidation path.
  private _resetDestination(): void {
    const control = this.destinationForm.controls.destination;
    control.setValue('', { emitEvent: false });
    control.markAsPristine();
    control.markAsUntouched();
    this.destinationError = null;
    this._picker?.reset();
    this._cdr.markForCheck();
  }

  private _openCancelConfirm(): void {
    const count = this._selection.selectionCount;
    const noun = count === 1 ? 'person' : 'people';
    const data: IAdminConfirmDialogData = {
      title: `Discard ${count} gathered ${noun}?`,
      body: `${count} ${noun} will be removed from this move and nothing will change.`,
      consequence: 'You can gather people again at any time.',
      confirmLabel: 'Discard',
      destructive: true,
    };

    const dialog_ref = this._dialog.open(AdminConfirmDialogComponent, {
      width: '480px',
      maxHeight: '90vh',
      data,
    });

    dialog_ref.componentInstance.confirm.pipe(
      takeUntilDestroyed(this._destroy_ref)
    ).subscribe(() => {
      dialog_ref.close(true);
      // Discard is not dismiss: the panel stays open and settles into its
      // empty state (AC15) rather than closing — closing is a separate,
      // operator-initiated action via the header close button.
      this._selection.discard();
      this._cdr.markForCheck();
    });
  }
}
